import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Inbox,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDate, fmtRelative, fmtUsd } from '@/lib/format';
import type { AgentRun, IngestedRLPSummary } from '@/lib/api-types';
import { SuperHero } from '@/components/SuperHero';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonLine } from '@/components/Skeleton';

const STATUS_STYLES: Record<string, { pill: string; icon: typeof CheckCircle2 }> = {
  success: { pill: 'pill-success', icon: CheckCircle2 },
  failed: { pill: 'pill-danger', icon: XCircle },
  running: { pill: 'pill-info', icon: Sparkles },
};

export default function Overview() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['overview'],
    queryFn: api.overview,
  });

  return (
    <div className="flex flex-col gap-10">
      <SuperHero data={data} isLoading={isLoading} />

      {isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Failed to load overview: {error instanceof Error ? error.message : 'unknown'}
        </div>
      )}

      {/* Activity + marginalia */}
      <section aria-label="Activity feed" className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="card flex flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-rule px-5 py-4">
            <div>
              <h2 className="text-[0.9375rem] font-semibold text-ink">Dispatch log</h2>
              <p className="text-xs text-ink-muted">Latest 15 agent runs</p>
            </div>
            <Link
              to="/agents"
              className="text-[0.8125rem] font-semibold text-brand-700 hover:text-brand-800"
            >
              View ledger →
            </Link>
          </div>
          <div className="px-5 py-2">
            {isLoading ? (
              <div className="flex flex-col gap-3 py-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonLine key={i} width={`${70 + ((i * 7) % 30)}%`} />
                ))}
              </div>
            ) : !data?.recent_runs || data.recent_runs.length === 0 ? (
              <EmptyState
                title="The wire is quiet"
                description="Once the hunter, extractor, scout, or outreach agents fire, their dispatches will appear here."
                icon={<Inbox className="h-5 w-5" />}
                className="border-0 shadow-none"
              />
            ) : (
              <ol data-testid="activity-feed">
                {data.recent_runs.map((r) => (
                  <DispatchRow key={r.id} run={r} />
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Marginalia — how the pipeline works */}
        <aside className="card flex flex-col gap-4 p-5">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand-700">
              <Sparkles className="h-4 w-4" />
            </div>
            <h3 className="text-[0.9375rem] font-semibold text-ink">How it works</h3>
          </div>
          <p className="text-[0.8125rem] leading-relaxed text-ink-muted">
            Four agents cycle in succession, each handing off to the next.
          </p>
          <ol className="flex flex-col gap-3">
            {[
              { n: '1', name: 'Hunter', desc: 'sweeps SAM.gov every 30 min' },
              { n: '2', name: 'Extractor', desc: 'parses RLP into requirements' },
              { n: '3', name: 'Scout', desc: 'sources candidate properties' },
              { n: '4', name: 'Writer', desc: 'drafts broker outreach' },
            ].map((step) => (
              <li key={step.n} className="flex items-start gap-3">
                <div className="mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-brand-600 text-[0.6875rem] font-bold text-white">
                  {step.n}
                </div>
                <div>
                  <div className="text-[0.8125rem] font-semibold text-ink">{step.name}</div>
                  <div className="text-xs text-ink-muted">{step.desc}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-2 rounded-lg border border-rule bg-surface-sunk px-3 py-2.5 text-[0.75rem] text-ink-muted">
            Refreshes every 30 min · ⌘R to refresh manually
          </div>
        </aside>
      </section>
    </div>
  );
}

function DispatchRow({ run }: { run: AgentRun }) {
  const style = STATUS_STYLES[run.status] ?? { pill: 'pill-neutral', icon: Sparkles };
  const Icon = style.icon;
  const ingested = run.ingested_rlps ?? [];
  const hasIngested = ingested.length > 0;

  return (
    <li className="dispatch-row flex-wrap items-start">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-sunk text-ink-muted">
        <Icon className="h-[14px] w-[14px]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[0.8125rem] font-semibold text-ink">{run.agent_name}</span>
          <span className={style.pill}>{run.status}</span>
          {hasIngested && (
            <span className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-kicker text-brand-700">
              + {ingested.length} new
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-ink-muted">
          {run.output_summary ?? run.error_message ?? '—'}
        </div>
        {hasIngested && <IngestedRlpList items={ingested} />}
      </div>
      <div className="hidden text-right sm:block">
        <div className="text-[0.8125rem] font-semibold tabular-nums text-ink">
          {run.cost_usd && run.cost_usd > 0 ? fmtUsd(run.cost_usd) : ''}
        </div>
        <div className="text-[0.6875rem] text-ink-dim">{fmtRelative(run.started_at)}</div>
      </div>
    </li>
  );
}

function IngestedRlpList({ items }: { items: IngestedRLPSummary[] }) {
  return (
    <ul className="mt-2 flex flex-col gap-1.5 border-l-2 border-brand-200 pl-3">
      {items.map((rlp) => {
        const dueSoon =
          rlp.response_deadline && rlp.response_deadline - Date.now() < 14 * 24 * 60 * 60_000;
        const locale =
          rlp.city && rlp.state ? `${rlp.city}, ${rlp.state}` : rlp.state ?? rlp.city ?? null;
        return (
          <li key={rlp.id} className="flex flex-col gap-0.5 rounded-md bg-surface-sunk/60 px-2.5 py-1.5">
            <Link
              to={`/rlps/${rlp.id}`}
              className="line-clamp-1 text-[0.8125rem] font-semibold text-ink hover:text-brand-700"
              title={rlp.title}
            >
              {rlp.title}
            </Link>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[0.6875rem] text-ink-muted">
              <span className="font-mono font-semibold text-ink-dim">{rlp.solicitation_number}</span>
              {rlp.agency && <span>· {rlp.agency}</span>}
              {locale && <span>· {locale}</span>}
              {rlp.response_deadline ? (
                <span className={dueSoon ? 'font-semibold text-warn-bold' : ''}>
                  · Due {fmtDate(rlp.response_deadline)}
                </span>
              ) : (
                <span>· No deadline listed</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
